/**
 * Connection Store for Ably Native Pub/Sub
 *
 * Simplified store since Ably SDK handles:
 * - Automatic reconnection (every 15s up to 2 min, then 30s)
 * - Message recovery on reconnect (2-minute window)
 * - Connection state management
 * - Heartbeat internally
 */

import { create } from 'zustand';
import { wsLogger } from '@/shared/lib/logger';
import { ABLY_CONNECTION_STATES } from '../config';
import {
  getOrCreateEchoInstance,
  disconnectEchoSingleton,
} from '../websocket-singleton';
import {
  setupChannelReattach,
  teardownChannelReattach,
} from '../channel-reattach';
import {
  resetForegroundReconcileThrottle,
  resetReconcileFetchThrottle,
} from '../reconcile-throttle';
import type { TypedEchoInstance } from '../types/echo.types';
// NOTE: connection.store deliberately does NOT import subscription.store.
// The reverse cleanup (clearing dangling subscriptions on disconnect) is
// wired from `subscription.store` itself via `useConnectionStore.subscribe`.
// Importing back here would create the cycle that madge keeps flagging.

export enum ConnectionStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  SUSPENDED = 'suspended', // Ably-specific: long-term disconnection
  FAILED = 'failed',
  UNAVAILABLE = 'unavailable', // Connection not available
}

interface ConnectionState {
  // Connection state
  status: ConnectionStatus;
  isConnected: boolean;
  lastError: string | null;

  // Connection details
  echo: TypedEchoInstance | null;
  userId: string | null;
  token: string | null;
  connectionId: string | null;

  // Connection timestamps
  lastConnectedAt: number | undefined;
  lastDisconnectedAt: number | undefined;

  // Pending reconnect timer
  reconnectTimeoutId: ReturnType<typeof setTimeout> | null;

  // Core Actions
  connect: (userId: string, token: string) => Promise<void>;
  disconnect: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  forceReconnect: () => void;

  // Computed
  getConnectionUptime: () => number;
  getLastConnectedAt: () => number | undefined;
  getLastDisconnectedAt: () => number | undefined;
}

export const useConnectionStore = create<ConnectionState>((set, get) => ({
  // Initial state
  status: ConnectionStatus.DISCONNECTED,
  isConnected: false,
  lastError: null,

  echo: null,
  userId: null,
  token: null,
  connectionId: null,

  // Connection timestamps
  lastConnectedAt: undefined,
  lastDisconnectedAt: undefined,

  // Pending reconnect timer
  reconnectTimeoutId: null,

  // Core Actions
  connect: async (userId: string, token: string) => {
    const state = get();

    // Skip connection if page is unloading or hidden
    if (typeof window !== 'undefined') {
      if (document.visibilityState === 'hidden' && !document.hasFocus()) {
        return;
      }
    }

    // Prevent concurrent connections
    if (state.status === ConnectionStatus.CONNECTING) {
      return;
    }

    if (state.echo) {
      // Check if connecting with same credentials
      if (state.userId === userId && state.token === token) {
        return;
      }

      // Different credentials - disconnect first
      state.disconnect();
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Generate unique connection ID
    const connectionId = `${userId}-${Date.now()}`;

    set({
      status: ConnectionStatus.CONNECTING,
      userId,
      token,
      connectionId,
    });

    try {
      wsLogger.info('Creating Ably Echo instance...', {
        userId: userId.substring(0, 8) + '...',
        hasToken: !!token,
      });

      // Get or create Echo instance using singleton pattern
      const echo = getOrCreateEchoInstance(userId, token);

      // Verify this is still the current connection attempt
      const currentState = get();
      if (currentState.connectionId !== connectionId) {
        wsLogger.warn('Connection ID mismatch, abandoning connection attempt');
        echo.disconnect();
        return;
      }

      // Pre-warm `user.${id}` channel with `rewind=2m` so the FIRST attach
      // replays data_changed frames missed in the last 2 minutes (parity
      // with APL-A.1 / AND-A.1, see docs/REALTIME_RECONCILE_PLAN.md).
      // Echo's later .private(name) call goes through
      // AblyConnector.privateChannel → channels.get(`private:${name}`)
      // without options, which returns the cached instance with rewind
      // already applied. Replayed events are filtered by ts in
      // use-realtime-sync so duplicates are safe.
      try {
        const ablyChannels = (
          echo as unknown as {
            connector?: {
              ably?: {
                channels?: {
                  get: (
                    name: string,
                    options?: { params?: Record<string, string> }
                  ) => unknown;
                };
              };
            };
          }
        )?.connector?.ably?.channels;
        ablyChannels?.get(`private:user.${userId}`, {
          params: { rewind: '2m' },
        });
      } catch (error) {
        wsLogger.warn('Failed to pre-warm user channel with rewind', {
          error: error instanceof Error ? error.message : String(error),
        });
      }

      // Channel-state observer with re-attach backoff (WEB-A.2). Ably-JS
      // auto-recovers `suspended`; `failed`/`detached` go silent without
      // a manual attach() — this helper handles the retry loop. Capped at
      // 3 attempts (2s/5s/15s); on exhaustion the connection-level state
      // flips to FAILED so the rest of the app stops trusting WS pushes.
      setupChannelReattach(echo, userId, (reason) => {
        set({
          status: ConnectionStatus.FAILED,
          isConnected: false,
          lastError: `Channel re-attach exhausted: ${reason}`,
        });
      });

      // Set up Ably connection state listeners
      // Ably SDK exposes connection state via connector.ably.connection
      const ablyConnection = (
        echo as unknown as {
          connector?: {
            ably?: {
              connection?: {
                on: (
                  callback: (stateChange: {
                    current: string;
                    previous: string;
                    reason?: { message?: string };
                  }) => void
                ) => void;
              };
            };
          };
        }
      )?.connector?.ably?.connection;
      if (ablyConnection) {
        ablyConnection.on(
          (stateChange: {
            current: string;
            previous: string;
            reason?: { message?: string };
          }) => {
            const { current, previous, reason } = stateChange;

            wsLogger.debug('Ably connection state changed', {
              from: previous,
              to: current,
              reason: reason?.message,
            });

            switch (current) {
              case ABLY_CONNECTION_STATES.CONNECTED:
                set({
                  status: ConnectionStatus.CONNECTED,
                  isConnected: true,
                  lastConnectedAt: Date.now(),
                  lastError: null,
                });
                break;

              case ABLY_CONNECTION_STATES.DISCONNECTED:
                // Temporary disconnection - Ably will auto-reconnect
                set({
                  status: ConnectionStatus.DISCONNECTED,
                  isConnected: false,
                  lastDisconnectedAt: Date.now(),
                });
                break;

              case ABLY_CONNECTION_STATES.SUSPENDED:
                // Long-term disconnection - Ably continues reconnecting
                set({
                  status: ConnectionStatus.SUSPENDED,
                  isConnected: false,
                  lastError: reason?.message || 'Connection suspended',
                });
                break;

              case ABLY_CONNECTION_STATES.FAILED:
                // Terminal failure - requires explicit reconnect
                set({
                  status: ConnectionStatus.FAILED,
                  isConnected: false,
                  lastError: reason?.message || 'Connection failed',
                });
                break;

              case ABLY_CONNECTION_STATES.CONNECTING:
                set({
                  status: ConnectionStatus.CONNECTING,
                });
                break;
            }
          }
        );
      }

      if (ablyConnection) {
        // Ably state listener will transition to CONNECTED when ready
        set({ echo });
      } else {
        // No Ably listener available — set CONNECTED as fallback
        set({
          echo,
          status: ConnectionStatus.CONNECTED,
          isConnected: true,
          lastConnectedAt: Date.now(),
        });
      }
    } catch (error) {
      wsLogger.error('Failed to connect WebSocket:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        userId: userId.substring(0, 8) + '...',
      });
      set({
        status: ConnectionStatus.FAILED,
        lastError: error instanceof Error ? error.message : 'Connection failed',
      });
    }
  },

  disconnect: () => {
    const state = get();

    // Clear any pending reconnect timeout
    if (state.reconnectTimeoutId) {
      clearTimeout(state.reconnectTimeoutId);
    }

    // Cancel channel-reattach loop (WEB-A.2) before tearing down Echo —
    // otherwise a pending setTimeout would call channel.attach() on a
    // freshly destroyed Ably instance.
    teardownChannelReattach();

    // Reset L2/L3 throttles so a fast logout→login doesn't gate the next
    // session's first reconcile fetch by the previous user's clock.
    resetReconcileFetchThrottle();
    resetForegroundReconcileThrottle();

    // Use singleton disconnect to properly clean up
    disconnectEchoSingleton();

    // Subscription store now wires its own cleanup via
    // `useConnectionStore.subscribe(...)` (see subscription.store bottom)
    // — flipping `isConnected` to false here triggers `clearAll()` there.

    set({
      echo: null,
      userId: null,
      token: null,
      connectionId: null,
      reconnectTimeoutId: null,
      status: ConnectionStatus.DISCONNECTED,
      isConnected: false,
      lastDisconnectedAt: Date.now(),
    });
  },

  setConnectionStatus: (status) =>
    set({
      status,
      isConnected: status === ConnectionStatus.CONNECTED,
    }),

  forceReconnect: () => {
    const state = get();
    const { userId, token } = state;

    // Don't reconnect if we're already connecting
    if (state.status === ConnectionStatus.CONNECTING) {
      return;
    }

    state.disconnect();

    if (userId && token) {
      // Short delay to allow cleanup
      const timeoutId = setTimeout(() => {
        set({ reconnectTimeoutId: null });
        get().connect(userId, token);
      }, 500);
      set({ reconnectTimeoutId: timeoutId });
    }
  },

  // Computed
  getConnectionUptime: () => {
    const state = get();
    if (!state.isConnected || !state.lastConnectedAt) return 0;
    return Date.now() - state.lastConnectedAt;
  },

  getLastConnectedAt: () => get().lastConnectedAt,
  getLastDisconnectedAt: () => get().lastDisconnectedAt,
}));
